/* eslint-disable global-require */
import React, {
  createContext,
  ReactNode,
  useReducer,
  useContext,
  useState,
  useEffect,
} from 'react';
import moment from 'moment-timezone';
import {
  getStandingOrderCode,
  mapAdmitDoctorsOptions,
  getOverrideAgency,
  getAttendingPhysicianDTO,
  isAgencyUserDoctorRoleGroup,
} from '@mummssoftware/utils/utils';
import { getPhysicians } from '@mummssoftware/utils/pimReader';
import { getActiveMedications } from '@mummssoftware/utils/medications/utils';
import { OrdersGlobalContext } from '@mummssoftware/global-context/OrdersGlobalContext';
import {
  formatPimDate,
  formatPostDate,
  formatDateYYYYMMDD,
} from '@mummssoftware/utils/formatters';
import {
  NONE,
  emptyText,
  error,
  CPF,
  emptyNoteText,
} from '@mummssoftware/utils/constants';
import {
  MMD,
  ADM,
  LOC,
  OTH,
  IA,
  VA,
  WA,
  PPN,
} from '@mummssoftware/utils/orders';
import { orderTypeMapping } from '@mummssoftware/utils/orders/orders-maps';
import {
  getUserCredentials,
  getLocalOrderTime,
  offsetDate,
} from '@mummssoftware/utils/orders/orders-utils';
import {
  GlobalContext,
  SET_GLOBAL_DOCTORS,
  SET_MEDS,
  SET_CARE_PLAN_FREQUENCIES,
  SET_PATIENT_SUBMITTED_CPP_LIST,
  SET_GENERAL_STANDING_ORDER,
} from '@mummssoftware/global-context/GlobalContext';
import { IDGGlobalContext, SET_LAST_IDG_INFO } from '@mummssoftware/idg';
import {
  PNGlobalContext,
  SET_ORDER_TYPES,
  SET_NEXT_IDG_MEETING,
} from '@mummssoftware/global-context/PNGlobalContext';
import {
  SET_ROLE_GROUPS,
  VFGlobalContext,
} from '@mummssoftware/global-context/VFGlobalContext';
import {
  hashRoleGroupList,
  processRoleGroups,
  getRoleGroups,
  mapRolesToOptions,
  getLastIdgInfo,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import { useIntl } from 'react-intl';
import { Context } from '../../context';
import {
  getLOCTypes,
  getDischargeTypes,
  getIDGmeetings,
  cancelWorkflowAction,
  resetWorkflowAction,
} from './utils';
import {
  SET_PECOS_OPEN,
  CLOSE_MENU,
  SET_ORDER_CREATED,
  SET_MENU_TYPE,
  GET_LOC_TYPES,
  GET_IDG_MEETING,
  GET_STANDING_ORDERS,
  SET_EFFECTIVE_DATE,
  SET_LOC_TYPE,
  SET_FACILITY_TYPE,
  SET_DISCHARGE_REASON,
  GET_DISCHARGE_TYPES,
  SAVE_PSO,
  SET_ORDER_DISABLED,
  SET_API_ERROR,
  SET_PSO_TEXT,
  SET_APPROVAL_DATE_ERROR,
  SET_FACILITY_OPTIONS,
  UPDATE_PN_COUNT,
  UPDATE_COMPLETE_MAINT_STATUS,
  SET_NOTE_STARTED,
  EMPTY_NOTE_TEXT,
  DischargeTypes,
  LOCType,
  Admit,
  pending,
  complete,
  verbalOrderType,
  PECOS_ELIGIBLE,
  PecosRes,
  admitOrderType,
  noteOrdersExpanded,
  Refer,
} from './constants';
import { isDisType } from '../Statements/utils';
import { durationList, makeFreqList } from './freq-constants';
import defaults from '../../translations/en.json';

// @types/rtf-to-html does not exist
// Could not find a declaration file for module rtf-parser
// @note leaving parseRTF in case needed
const parseRTF = require('@mummssoftware/rtf-parser');
const rtfToHTML = require('@mummssoftware/rtf-to-html');

type PnPayload = {
  refreshEntity: string;
  refreshEntityKey: string;
};

export type ProgressNotesPSOData = {
  [standingOrderId: string]: mumms.PatientStandingOrderDTOv2;
};

type ProgressNotesContextProviderProps = {
  children: ReactNode;
};

type APIerror = { message: string; severity: string };
type ProgressNotesContext = {
  closeMenu: boolean | null;
  savePSO: boolean | null;
  orderCreated: boolean | null;
  menuType: mumms.MenuType | null;
  orderTypes: mumms.OrderType[] | null;
  dischargeTypes: DischargeTypes | null;
  locTypes: mumms.OptionType[] | null;
  idgMeeting: any | null;
  LOCtype: LOCType | null;
  dischargeMapping: any | null;
  disOrderMap: any | null;
  standingOrders: mumms.StandingOrder | null;
  effectiveDate: Date | number | null;
  cancelWorkflow: any | null;
  resetWorkflow: any | null;
  closeTagMenu: any | null;
  carePlanFrequencies: any | null;
  medications: any | null;
  orderDisabled: boolean;
  apiError: APIerror | null;
  patientCarePlanProblems: mumms.patientCarePlanProblem[] | null;
  ordersGlobalMap: Record<string, mumms.PatientStandingOrderDTOv2>;
  ordersGlobalMapEffectiveDate?: ProgressNotesPSOData[] | null;
  psoText: string;
  admitPhysicianList: mumms.Doctor[] | [];
  approvalDateError: boolean;
  updatePnCount: Record<string, PnPayload>;
  facilityOptions: mumms.OptionType[] | [];
  // frequencyOptions: mumms.OptionType[] | [];
  periodOptions: mumms.OptionType[] | [];
  roleGroupOptions: mumms.RoleGroup[] | [];
  dischargeReason: mumms.OptionType | undefined;
  facilityType: mumms.OptionType | undefined;
  noteStarted: boolean;
  emptyNote: boolean;
  pickerDialogHeight: number;
  rgHash: null | mumms.HashedRoleGroup;
  isOverrideVerifOrderAgency: boolean;
  checkIfPECOSEligible: any;
  pecosOpen: boolean;
  attendingPhysician: mumms.physicianDTO | undefined | null;
  pecosPhysicians: any;
  updateDisMaintainStatus: any;
  isPhysicianRoleGroup: boolean;
  handleGenericOrder: () => any;
  handleSendApproval: () => any;
  dispatch?: any;
};

type ProgressNotesContextState = {
  closeMenu: boolean | null;
  savePSO: boolean | null;
  orderCreated: boolean | null;
  menuType: mumms.MenuType | null;
  locTypes: mumms.OptionType[] | null;
  idgMeeting: any | null;
  LOCtype: LOCType | null;
  dischargeTypes: DischargeTypes | null;
  orderTypes: mumms.OrderType[] | null;
  standingOrders: mumms.StandingOrder | null;
  effectiveDate: Date | number | null;
  cancelWorkflow: any | null;
  resetWorkflow: any | null;
  carePlanFrequencies: any | null;
  medications: any | null;
  closeTagMenu: any | null;
  orderDisabled: boolean;
  apiError: APIerror | null;
  psoText: string;
  approvalDateError: boolean;
  updatePnCount: Record<string, PnPayload>;
  facilityOptions: mumms.OptionType[] | [];
  dischargeReason: mumms.OptionType | undefined;
  facilityType: mumms.OptionType | undefined;
  completeMaintStatus: boolean;
  noteStarted: boolean;
  emptyNote: boolean;
  pecosOpen: boolean;
};

const initialState = {
  closeMenu: null,
  savePSO: null,
  orderCreated: null,
  menuType: null,
  orderTypes: null,
  locTypes: null,
  LOCtype: null,
  dischargeTypes: null,
  standingOrders: null,
  effectiveDate: null,
  cancelWorkflow: null,
  resetWorkflow: null,
  carePlanFrequencies: null,
  medications: null,
  idgMeeting: null,
  closeTagMenu: null,
  apiError: null,
  orderDisabled: true,
  psoText: emptyText,
  updatePnCount: {},
  approvalDateError: false,
  facilityOptions: [],
  dischargeReason: undefined,
  facilityType: undefined,
  completeMaintStatus: false,
  noteStarted: false,
  emptyNote: false,
  pecosOpen: false,
};

const initProgressNotesContext = {
  closeMenu: null,
  savePSO: null,
  orderCreated: null,
  menuType: null,
  locTypes: null,
  LOCtype: null,
  orderTypes: null,
  dischargeTypes: null,
  standingOrders: null,
  effectiveDate: null,
  cancelWorkflow: null,
  resetWorkflow: null,
  carePlanFrequencies: null,
  medications: null,
  idgMeeting: null,
  closeTagMenu: null,
  orderDisabled: true,
  apiError: null,
  psoText: emptyText,
  updatePnCount: {},
  approvalDateError: false,
  facilityOptions: [],
  dischargeReason: undefined,
  facilityType: undefined,
  noteStarted: false,
  emptyNote: false,
  pecosOpen: false,
};

export const ProgressNotesContextReducer = (
  state: ProgressNotesContextState,
  action: { type: string; payload: mumms.ComponentName | any },
) => {
  let newState = { ...state };
  switch (action.type) {
    case CLOSE_MENU:
      newState.closeMenu = action.payload;
      break;
    case SET_MENU_TYPE:
      newState.menuType = action.payload;
      break;
    case SET_EFFECTIVE_DATE:
      newState.effectiveDate = action.payload;
      break;
    case GET_STANDING_ORDERS:
      newState.standingOrders = action.payload;
      break;
    case SET_ORDER_CREATED:
      newState.orderCreated = action.payload;
      break;
    case GET_DISCHARGE_TYPES:
      newState.dischargeTypes = action.payload;
      break;
    case GET_LOC_TYPES:
      newState.locTypes = action.payload;
      break;
    case GET_IDG_MEETING:
      newState.idgMeeting = action.payload;
      break;
    case SET_ORDER_DISABLED:
      newState.orderDisabled = action.payload;
      break;
    case SAVE_PSO:
      newState.savePSO = action.payload;
      break;
    case SET_LOC_TYPE:
      newState.LOCtype = action.payload;
      break;
    case SET_API_ERROR:
      newState.apiError = action.payload;
      break;
    case SET_APPROVAL_DATE_ERROR:
      newState.approvalDateError = action.payload;
      break;
    case SET_PSO_TEXT:
      newState.psoText = action.payload;
      break;
    case SET_FACILITY_OPTIONS:
      newState.facilityOptions = action.payload;
      break;
    case SET_DISCHARGE_REASON:
      newState.dischargeReason = action.payload;
      break;
    case SET_FACILITY_TYPE:
      newState.facilityType = action.payload;
      break;
    case SET_NOTE_STARTED:
      newState.noteStarted = action.payload;
      break;
    // todo
    case EMPTY_NOTE_TEXT:
      newState.emptyNote = action.payload;
      if (action.payload) {
        newState.noteStarted = false;
        // todo
        // newState.noteText = emptyText;
      }
      break;
    case UPDATE_COMPLETE_MAINT_STATUS:
      newState.completeMaintStatus = action.payload;
      break;
    case SET_PECOS_OPEN:
      newState.pecosOpen = action.payload;
      break;
    case UPDATE_PN_COUNT:
      // eslint-disable-next-line no-lone-blocks
      {
        if (action.payload) {
          if (action.payload.refreshEntity) {
            const entity = action.payload.refreshEntity;
            if (newState.updatePnCount[entity]) {
              delete newState.updatePnCount[entity];
            } else {
              newState.updatePnCount[entity] = action.payload;
            }
          }
        } else {
          newState.updatePnCount = {};
        }
      }
      break;
    default:
      newState = state;
      break;
  }
  return newState;
};

export const ProgressNotesContext = createContext<ProgressNotesContext>(
  initProgressNotesContext as ProgressNotesContext,
);
export const ProgressNotesContextProvider = ({
  children,
}: ProgressNotesContextProviderProps) => {
  const [
    {
      closeMenu,
      menuType,
      standingOrders,
      effectiveDate,
      orderCreated,
      dischargeTypes,
      locTypes,
      LOCtype,
      orderDisabled,
      savePSO,
      apiError,
      psoText,
      approvalDateError,
      updatePnCount,
      facilityOptions,
      facilityType,
      dischargeReason,
      completeMaintStatus,
      noteStarted,
      emptyNote,
      pecosOpen,
    },
    dispatch,
  ] = useReducer(ProgressNotesContextReducer, initialState);
  const {
    observation,
    agency,
    workflow,
    clearScripts,
    pim,
    scheduler,
    patient,
    currentUser,
    clearSavedNote,
    updateMaintStatus,
    savedApproval,
    updateProgressNotes,
    keycloak,
    entityKey,
    idgMeetingId,
    saveNoteTextInContext,
    saveInProcessProgressNote,
    hasMedOrderError,
    doMedOrderCleanup,
    updateMedOrderError,
    medOrderError,
    admitErrorCount,
    siteId,
    doCountError,
    sendApproval,
    saveApprovalDTO,
    smallWidget,
  } = useContext(Context);
  const {
    medsGlobal,
    doctorsGlobal,
    carePlanFrequenciesGlobal,
    patientSubmittedCPPListGlobal,
    patientStandingOrdersGlobal,
    patientGlobal,
  } = useContext(GlobalContext);
  const intl = useIntl();
  const medNPOrderDoctorBins: string = window.env
    ?.medNPOrderDoctorBins as string;
  const medPAOrderDoctorBins: string = window.env
    ?.medPAOrderDoctorBins as string;
  const isPhysicianRoleGroup: boolean = isAgencyUserDoctorRoleGroup(
    currentUser,
    medNPOrderDoctorBins,
    medPAOrderDoctorBins,
  ) as boolean;
  const {
    orderType,
    convoType,
    admitPhysician,
    savedNote,
    verbalDate,
    orderStatement,
    cancelOrderWorkflow,
  } = useContext(OrdersGlobalContext);

  const { dispatchVF, roleGroupsGlobal } = useContext(VFGlobalContext);
  const { nextIDGMeeting, orderTypesGlobal, refreshPNData } = useContext(
    PNGlobalContext,
  );

  const globalContext = useContext(GlobalContext);
  const pnGlobalContext = useContext(PNGlobalContext);
  const { dispatchIDG, lastidgContent } = useContext(IDGGlobalContext);

  const textAreaGroupID = 'text-area-group';

  const textWidgetHeight =
    (document &&
      document.getElementById(textAreaGroupID) &&
      document.getElementById(textAreaGroupID)?.clientHeight) ||
    0;

  const pnWidgetHeight =
    (document &&
      document.getElementById(noteOrdersExpanded) &&
      document.getElementById(noteOrdersExpanded)?.clientHeight) ||
    0;

  useEffect(() => {
    const areaHeight =
      textWidgetHeight > 100 ? textWidgetHeight - 30 : textWidgetHeight;

    const dialogHeight = pnWidgetHeight - areaHeight;
    let selectDialogHeight = dialogHeight > 10 ? dialogHeight : 100;

    /* Phone / narrow tiles: keep physician (and other) suggest menus tall enough to scroll comfortably */
    const narrowUi =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 600px)').matches;
    const minMenuListPx = narrowUi ? 280 : 100;
    selectDialogHeight = Math.max(selectDialogHeight, minMenuListPx);

    setPickerDialogHeight(selectDialogHeight);
  }, [textWidgetHeight, pnWidgetHeight]);

  const siteID = siteId || patient.siteId.toString();
  const attPhysician = patientGlobal && getAttendingPhysicianDTO(patientGlobal);

  // initialize LOC
  const [patientLOCtype, setPatientLOCType] = useState<LOCType>(
    LOCtype || (patient?.loc as LOCType) || 'RTN',
  );
  const [pickerDialogHeight, setPickerDialogHeight] = useState<number>(100);
  const [ordertypes, setOrderTypes] = useState<mumms.OrderType[] | any>(null);
  const [dischargetypes, setDischargeTypes] = useState<any>(null);
  const [loctypes, setLocTypes] = useState<any>(null);
  const [dischargemapping, setDischargeMapping] = useState<any>(null);
  const [isDisOrderMap, setIsDisOrderMap] = useState<any>(null);
  const [planFrequencies, setPlanFrequencies] = useState<any>(null);
  const [activeMeds, setActiveMeds] = useState<any>(null);
  const [patientCarePlanProblems, setPatientCarePlanProblems] = useState<any>(
    null,
  );

  const setIDGMeeting = (meeting: mumms.NextIDGMeeting) =>
    pnGlobalContext.dispatchPN({
      type: SET_NEXT_IDG_MEETING,
      payload: meeting,
    });
  const [standingorders, setStandingOrders] = useState<
    mumms.StandingOrder | null | any
  >(null);

  const ordersGlobalMap: Record<number, mumms.PatientStandingOrderDTOv2> =
    patientStandingOrdersGlobal &&
    patientStandingOrdersGlobal.reduce(
      (seed: any, stdOrder: mumms.PatientStandingOrderDTOv2) => {
        seed[stdOrder.id.toString()] = stdOrder.templatebasedtext;
        return seed;
      },
      {},
    );

  const ordersGlobalMapEffectiveDate: ProgressNotesPSOData[] | null =
    patientStandingOrdersGlobal &&
    patientStandingOrdersGlobal.map(
      (stdOrder: mumms.PatientStandingOrderDTOv2) => ({
        [stdOrder.id.toString()]: {
          ...stdOrder,
        },
      }),
    );

  const getStandingOrder = async () => {
    const standingOrderCode = getStandingOrderCode(patient);
    const orders: any = await observation.getStandingOrders(
      patient.patientNumber,
      agency,
      standingOrderCode as mumms.StandingOrderType,
    );
    if (!(orders instanceof Response) && orders && orders.length) {
      // update Global Context
      globalContext.dispatch({
        type: SET_GENERAL_STANDING_ORDER,
        payload: orders,
      });
      const ordersbyLOC = orders[0];
      // todo for testing change it
      if (ordersbyLOC.isinuse === 'false') {
        const decoder = new TextDecoder('utf-8');

        ordersbyLOC.shortname = standingOrderCode;

        pim
          .getPSOTemplate(siteID, agency, standingOrderCode)
          .then((response: any) => {
            if (response.status === 200) {
              const reader = response.body.getReader();
              let html: any = '';

              reader.read().then(function processText({ done, value }: any) {
                if (done) {
                  if (ordersbyLOC.html.includes('rtf1')) {
                    // This is an RTF file
                    rtfToHTML.fromString(
                      ordersbyLOC.html,
                      (err: any, parsed: any) => {
                        if (err) {
                          dispatch({
                            type: SET_API_ERROR,
                            payload: {
                              message: defaults.issueParsingSO,
                              severity: error,
                            },
                          });
                        }
                        ordersbyLOC.templatebasedtext = parsed;
                        setStandingOrders(ordersbyLOC);
                        dispatch({
                          type: SET_PSO_TEXT,
                          payload: ordersbyLOC.templatebasedtext,
                        });

                        // https://www.npmjs.com/package/@iarna/rtf-to-html
                        // prints a document containing:
                        // <p><strong>hi there</strong></p>
                      },
                    );
                    return;
                  }
                  // else this is plain text
                  ordersbyLOC.templatebasedtext = ordersbyLOC.html;
                  setStandingOrders(ordersbyLOC);
                  dispatch({
                    type: SET_PSO_TEXT,
                    payload: ordersbyLOC.templatebasedtext,
                  });
                  return;
                }
                html += decoder.decode(value);
                ordersbyLOC.html = html;

                // eslint-disable-next-line consistent-return
                return reader.read().then(processText);
              });
            } else {
              dispatch({
                type: SET_API_ERROR,
                payload: {
                  message: `${response.status}: ${defaults.issueRetrievingSO}`,
                  severity: error,
                },
              });
            }
          });
        // if (ordersbyLOC.isinuse === 'true')
      } else {
        setStandingOrders(ordersbyLOC);
        dispatch({
          type: SET_PSO_TEXT,
          payload: ordersbyLOC.templatebasedtext,
        });
      }
    }
    if (orders instanceof Response) {
      dispatch({
        type: SET_API_ERROR,
        payload: {
          message: `${orders.status}: ${defaults.issueRetrievingSO}`,
          severity: error,
        },
      });
    }
  };

  const cancelWorkflowFunction = () => {
    if (orderType === ADM && admitErrorCount < 5 && doCountError) {
      // don't anything, try API call 5 times
    } else {
      cancelOrderWorkflow();
      cancelWorkflowAction(dispatch);
      // reset the saved Note
      clearSavedNote();
    }
  };

  const closeTagMenu = () => {
    setTimeout(() => {
      dispatch({
        type: CLOSE_MENU,
        payload: true,
      });
    }, 300);
    setTimeout(() => {
      dispatch({
        type: SET_MENU_TYPE,
        payload: null,
      });
      // need to reset the CLOSE_MENU to NULL so that useEffect catches the change
      dispatch({
        type: CLOSE_MENU,
        payload: null,
      });
    }, 600);
  };

  const resetWorkflow = () => {
    cancelOrderWorkflow();
    resetWorkflowAction(dispatch, emptyText);
    saveNoteTextInContext(false);
  };

  const getOrderTypes = async () => {
    const orderstypes =
      orderTypesGlobal || (await workflow.getOrderTypes(agency));
    if (!(orderstypes instanceof Response)) {
      pnGlobalContext.dispatchPN({
        type: SET_ORDER_TYPES,
        payload: orderstypes,
      });
      // patient.currentStatus === Admitted
      const admitOrderTypes = orderstypes.filter(
        (order: any) => order.name !== Admit,
      );
      setOrderTypes(admitOrderTypes);
    }
  };

  const getMeds = async () => {
    const meds =
      medsGlobal ||
      (await clearScripts.getMedsAndPrescriptions(patient.patientNumber));
    if (!(meds instanceof Response)) {
      !medsGlobal &&
        globalContext.dispatch({
          type: SET_MEDS,
          payload: meds,
        });
      const currentMedications =
        Array.isArray(meds) && meds.length
          ? meds.filter((med): any => (med as any).medindex === '0')
          : [];
      const activeMedications = getActiveMedications(
        [...(currentMedications as any)],
        Date.now(),
      );
      const orderedActiveMeds = [
        ...(activeMedications as mumms.Medication[]),
      ].sort((a, b) => a.drugdescription.localeCompare(b.drugdescription));
      setActiveMeds(orderedActiveMeds);
    }
  };

  const getCarePlanFrequencies = async () => {
    const schedulerFrequenciesResponse =
      carePlanFrequenciesGlobal ||
      (await scheduler.getPatientCarePlanFrequencies(
        agency,
        siteID,
        agency,
        patient.patientNumber,
      ));

    if (!(schedulerFrequenciesResponse instanceof Response)) {
      // if on INIT no data in Global Context then dispatch
      !carePlanFrequenciesGlobal &&
        globalContext.dispatch({
          type: SET_CARE_PLAN_FREQUENCIES,
          payload: schedulerFrequenciesResponse,
        });
    }
  };

  const updatePlanFrequencies = (schedulerFrequenciesRes: any) => {
    // OP-30540: remove enddate from newest cpf
    // also, only show cpf if it is the current active version of the frequency
    const carePlanFrequenciesResponse = (schedulerFrequenciesRes as any).filter(
      (x: any) =>
        moment(x.effectiveDate).isSameOrBefore(moment(new Date())) &&
        (x.endDate === null || moment(new Date()).isBefore(x.endDate)),
    );
    setPlanFrequencies(carePlanFrequenciesResponse);
  };

  const getPatientCarePlanProblems = async () => {
    // @note list of patient CPP Submitted - excludes WIP and Resolved (not heavy API call)
    const latest = 'true';
    const mode: mumms.CPPMode = NONE;
    const patientCPP =
      patientSubmittedCPPListGlobal ||
      (await observation.getPatientCarePlanProblemsv2(
        patient.patientNumber,
        latest,
        mode,
        agency,
      ));
    if (!(patientCPP instanceof Response)) {
      // if on INIT no data in Global Context then dispatch
      !patientSubmittedCPPListGlobal &&
        globalContext.dispatch({
          type: SET_PATIENT_SUBMITTED_CPP_LIST,
          payload: patientCPP,
        });
      const orderedCarePlans = [
        ...(patientCPP as mumms.patientCarePlanProblem[]),
      ].sort((a, b) =>
        a.careplanproblemshortname.localeCompare(b.careplanproblemshortname),
      );
      setPatientCarePlanProblems(orderedCarePlans);
    }
  };

  const [admitPhysicianList, setAdmitPhysicianList] = useState<
    mumms.Doctor[] | []
  >([]);
  const isAdmitType = orderType === ADM;

  const getDoctors = async (type: string) => {
    const doctorType = type === ADM ? ADM : LOC;
    const pecosDate =
      type === ADM && verbalDate ? formatDateYYYYMMDD(verbalDate) : undefined;
    const allDoctors = await getPhysicians(
      keycloak as any,
      agency,
      patient,
      doctorType,
      pecosDate,
    );
    if (!(allDoctors instanceof Response)) {
      if (isAdmitType) {
        const admitDoctors = mapAdmitDoctorsOptions(allDoctors);
        setAdmitPhysicianList(admitDoctors);
      } else {
        globalContext.dispatch({
          type: SET_GLOBAL_DOCTORS,
          payload: allDoctors,
        });
      }
    }
  };

  const [durationOptions, setDurationOptions] = useState<mumms.OptionType[]>(
    durationList,
  );
  /**
   * Only Nurses and Aides should get the ipu period option.
   */
  // const checkPeriodOptions = () => {
  //   if (roleGroup) {
  //     // If its not the TwentyFourHour Period show frequency no mater what
  //     // otherwise, if its a nurse and 24h period, ok to show it.
  //     const showIPUPeriod =
  //       (NurseRoleGroupId === roleGroup.id ||
  //         AideRoleGroupId === roleGroup.id) &&
  //       patient.currentFacilityQcode === Q5006;

  //     // eslint-disable-next-line consistent-return, array-callback-return
  //     const list = durationList.filter((x) => {
  //       if (x.value === TwentyFourHours) {
  //         if (showIPUPeriod) {
  //           return x;
  //         }
  //       } else {
  //         return x;
  //       }
  //     });
  //     setDurationOptions(list);
  //   }
  // };

  /**
   * check IDG
   */
  const checkIDGData = async () => {
    if (!lastidgContent) {
      const lastIDGData = await getLastIdgInfo(
        keycloak,
        agency,
        patient.patientNumber,
      );
      dispatchIDG &&
        dispatchIDG({
          type: SET_LAST_IDG_INFO,
          payload: lastIDGData,
        });
    }
  };

  // care plan frequency orders
  const [rgHash, setRgHash] = useState<mumms.HashedRoleGroup | null>(null);
  /**
   * get Frequency Order Role Group
   */
  const generateRoleInformation = async () => {
    const roleGroupList =
      // check VFGlobalContext first
      roleGroupsGlobal || (await getRoleGroups(keycloak, agency));
    // if not in VFGlobalContext then dispatch
    if (!roleGroupsGlobal) {
      const roleGroupOptions = mapRolesToOptions(roleGroupList);
      dispatchVF({
        type: SET_ROLE_GROUPS,
        payload: roleGroupOptions,
      });
    }

    const rgHashTemp = hashRoleGroupList(roleGroupList);
    const [x, rgHashRes] = await processRoleGroups(
      carePlanFrequenciesGlobal,
      rgHashTemp,
    );
    setRgHash(rgHashRes);
  };

  const [pecosPhysicians, setPecosPhysicians] = useState<PecosRes | null>(null);

  /**
   * checkIfPECOSEligible
   -> If the attending physician is NULL
   -> If the ordering physician is not PECOS eligible, display an error and the admission is NOT processed.
   -> If the ordering physician is PECOS eligible and...
      the attending physician is PECOS eligible and the attending physician is NOT the ordering physician, add the attending physician verbiage to the order
      the attending physician is NOT PECOS eligible, display a warning and do NOT add the attending physician verbiage to the order
   */

  const checkIfPECOSEligible = async () => {
    let npiCheck: any;
    if (
      isPhysicianRoleGroup &&
      orderType !== ADM &&
      patient.patientPossibleActions &&
      (patient.patientPossibleActions.includes(Admit) ||
        patient.patientPossibleActions.includes('Refer'))
    ) {
      npiCheck = currentUser?.npi;
    } else {
      npiCheck = admitPhysician?.npi;
    }
    // attPhysician is PIM physician
    // admitPhysician is selected in the admit order, this is the ordering physician
    if (effectiveDate && npiCheck) {
      const checkApiDate = formatDateYYYYMMDD(effectiveDate);
      // case 1 attPhysician is NULL
      if (!attPhysician) {
        const res = await pim.checkIfPECOSEligible(
          [npiCheck],
          checkApiDate,
          agency,
        );
        if (!(res instanceof Response) && res.length) {
          const admitResponse: any = res[0];
          if (admitResponse.eligibilityStatus === PECOS_ELIGIBLE) {
            const pecosAns = {
              admitOk: true,
              admitPhysician:
                admitResponse.eligibilityStatus === PECOS_ELIGIBLE,
              attPhysician: false,
              samePhys: false,
            };
            setPecosPhysicians(pecosAns);
            return pecosAns;
          }
          return false;
        }
        return false;
      }
      // case 2 there is an attending physician
      if (attPhysician?.npi) {
        // check for admit physician and attending physicians
        const res = await pim.checkIfPECOSEligible(
          [npiCheck, attPhysician.npi],
          checkApiDate,
          agency,
        );

        if (!(res instanceof Response) && res.length) {
          // attending physician and ordering physician are different
          if (res.length === 2) {
            // ordering physician
            const admitResponse: any = (res as any).find(
              (status: any) => status.npi === npiCheck,
            );
            // attending physician
            const attResponse: any = (res as any).find(
              (status: any) => status.npi === attPhysician.npi,
            );

            if (admitResponse.eligibilityStatus === PECOS_ELIGIBLE) {
              const pecosAns = {
                admitOk: true,
                admitPhysician:
                  admitResponse.eligibilityStatus === PECOS_ELIGIBLE,
                attPhysician: attResponse.eligibilityStatus === PECOS_ELIGIBLE,
                samePhys: false,
              };
              setPecosPhysicians(pecosAns);
              return pecosAns;
            }
            return false;
          }
          if (res.length === 1) {
            const admitResponse: any = res[0];

            if (admitResponse.eligibilityStatus === PECOS_ELIGIBLE) {
              const pecosAns = {
                admitOk: true,
                admitPhysician:
                  admitResponse.eligibilityStatus === PECOS_ELIGIBLE,
                attPhysician:
                  admitResponse.eligibilityStatus === PECOS_ELIGIBLE,
                samePhys: true,
              };
              setPecosPhysicians(pecosAns);
              return pecosAns;
            }
            return false;
          }
        }
        return false;
      }
    }

    return false;
  };

  const handleGenericOrder = async () => {
    const {
      id,
      noteddatetime,
      personnumber,
      patientnumber,
      siteid,
      note,
    } = savedNote as mumms.progressNote;

    if (orderType === OTH) {
      // save entityKey OTH
      const progressNoteEntityData = {
        entitykey: orderType as string,
        entityname: orderType,
        entitytypeshortname: orderType,
        patientprogressnoteid: savedNote?.id,
        createdatetime: formatPostDate(new Date()),
        createpersonid: savedNote?.personid,
      };

      await observation.saveProgressNoteEntity(
        progressNoteEntityData as any,
        agency,
        'POST',
      );
    }
    const ordernPSOText =
      note === emptyNoteText ? orderStatement : `${orderStatement}\n\n${note}`;

    const progressNoteData = {
      id,
      modifieddatetime: formatPostDate(new Date()),
      modifiedpersonnumber: personnumber,
      note: ordernPSOText as string,
      noteddatetime,
      patientnumber,
      personnumber,
      patientprogressnoteentitydto: null,
      siteid,
    };
    const method = 'PUT';
    const ignoreentity = 'true';
    const order = true;
    orderStatement &&
      saveInProcessProgressNote(
        agency,
        progressNoteData,
        method,
        ignoreentity,
        order,
      );
  };

  const isDISOrder =
    orderType &&
    isDisType(orderType) &&
    isDisOrderMap &&
    isDisOrderMap[orderType];

  const getInitStatus = (): mumms.ApprovalStatus =>
    orderType === 'MMD' ? IA : VA;
  const getInitPhysStatus = (): mumms.ApprovalStatus =>
    orderType === 'MMD' ? IA : WA;

  const handleSendApproval = async () => {
    // First API call is sendApproval
    let approvalData: any = {
      // the status is incomplete, it will be VA when final posting of note
      approvalstatusshortname: getInitStatus(),
      createdatetime: formatPostDate(Date.now()),
      verbaldatetime: verbalDate
        ? formatPostDate(offsetDate(verbalDate))
        : formatPostDate(Date.now()),
      entityid: savedNote?.id as number,
      entitytypeshortname: PPN as mumms.ApprovalEntityType,
      entityordertypeshortname: orderTypeMapping[
        orderType as string
      ] as mumms.EntityOrderType,
      id: '',
      patientid: patient.patientId as number,
      patientnumber: patient.patientNumber as string,
      physicianpersonid: (admitPhysician as any)?.id,
      reason: orderTypeMapping[orderType as string] as string,
      requestorpersonid: currentUser.userId as string,
      siteid: siteId || patient.siteId.toString(),
    };

    if (isPhysicianRoleGroup && orderType !== ADM) {
      const postDate = formatPostDate(new Date());
      const userCredentials = getUserCredentials(currentUser);
      const tzDate = getLocalOrderTime(currentUser);
      const physOrderVerifiedPayload = intl.formatMessage(
        defaults.physOrderVerified,
        {
          userCredentials,
          signDate: tzDate,
        },
      );
      const physData = {
        approvalstatusshortname: getInitPhysStatus(),
        physiciannote: physOrderVerifiedPayload,
        recordedbypersonid: currentUser.userId.toString(),
        signaturedatetime: postDate,
        physicianpersonfirstname: currentUser.firstName,
        physicianpersonlastname: currentUser.lastName,
        physicianpersonnumber: currentUser.personNumber,
      };
      approvalData = { ...approvalData, ...physData };
    }

    if (isDISOrder) {
      // should POST approval after discharge success
      saveApprovalDTO(approvalData);
    } else {
      sendApproval(agency, approvalData, 'POST');
    }
  };

  useEffect(() => {
    if (!ordertypes) {
      getOrderTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (emptyNote) {
      setTimeout(() => {
        dispatch({
          type: EMPTY_NOTE_TEXT,
          payload: false,
        });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emptyNote]);

  useEffect(() => {
    if (LOCtype) {
      setPatientLOCType(LOCtype);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOCtype]);

  useEffect(() => {
    // update at all times
    if (standingOrders) {
      getStandingOrder();
    }
    if (!standingOrders) {
      setStandingOrders(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingOrders, patientLOCtype]);

  useEffect(() => {
    if (locTypes && !loctypes) {
      getLOCTypes(pim, dispatch, patient, orderType, setLocTypes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locTypes]);

  useEffect(() => {
    if (patient) {
      if (orderType) {
        if (!doctorsGlobal && !isAdmitType) {
          if (!smallWidget) {
            getDoctors(orderType);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    if (patient) {
      if (orderType) {
        if (verbalDate && isAdmitType && !admitPhysicianList.length) {
          getDoctors(orderType);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, verbalDate]);

  useEffect(() => {
    if (!orderType && !admitPhysicianList.length) {
      setAdmitPhysicianList([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, verbalDate]);

  // PATIENT CPP get updated in Care Plan Problems Widget
  useEffect(() => {
    if (patient) {
      getPatientCarePlanProblems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientSubmittedCPPListGlobal]);

  // CPF FREQUENCIES get updated in Visit Frequency Widget
  useEffect(() => {
    if (patient) {
      getCarePlanFrequencies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlanFrequenciesGlobal]);

  useEffect(() => {
    if (carePlanFrequenciesGlobal) {
      updatePlanFrequencies(carePlanFrequenciesGlobal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carePlanFrequenciesGlobal && carePlanFrequenciesGlobal.length]);

  // MEDS get updated in Meds Widget
  useEffect(() => {
    if (patient) {
      getMeds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medsGlobal]);

  /* 
   Upon mounting fetch the next IDG meeting and which discharge options are
   configured as orders (OP-32052). Calling these sequentially to stop mangling params,
   chain more calls as needed with form:
  
   if (conditionA && conditionB) {
      mountPromiseChain.then(() =>
       apiCall();
      );
    }
   */
  useEffect(() => {
    const mountPromiseChain = Promise.resolve();

    if (!nextIDGMeeting && patient) {
      mountPromiseChain.then(() =>
        getIDGmeetings(patient.patientNumber, pim, dispatch, setIDGMeeting),
      );
    }
    // todo many api call
    mountPromiseChain.then(() =>
      getDischargeTypes(
        pim,
        dispatch,
        setDischargeTypes,
        setDischargeMapping,
        setIsDisOrderMap,
      ),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // REFRESH_PN_DATA
  useEffect(() => {
    if (!entityKey && refreshPNData.count) {
      if (refreshPNData.scroll) {
        updateProgressNotes(
          patient.patientNumber,
          agency,
          refreshPNData.scroll,
        );
      } else {
        updateProgressNotes(patient.patientNumber, agency);
      }
    }
    // IDG-filtered view: refresh widget only; do not overwrite PNGlobalContext via SET_PROGRESS_NOTES.
    // When entityKey is unset ("All notes"), the branch above handles refresh — avoid a second call
    // that used to always use the IDG print API while idgMeetingId was still set.
    if (idgMeetingId && entityKey && refreshPNData.count) {
      if (refreshPNData.scroll) {
        updateProgressNotes(
          patient.patientNumber,
          agency,
          refreshPNData.scroll,
          true,
        );
      } else {
        updateProgressNotes(patient.patientNumber, agency, undefined, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPNData && refreshPNData.count]);

  useEffect(() => {
    if (medOrderError) {
      resetWorkflow();
    }
  }, [medOrderError]);
  // todo delete

  const updateDisMaintainStatus = () => {
    let orderingphysicianid = admitPhysician?.id.toString() || '';
    let orderingphysicianpersonnumber = admitPhysician?.personNumber || '';
    if (isPhysicianRoleGroup) {
      orderingphysicianid = currentUser?.npi;
      orderingphysicianpersonnumber = currentUser?.personNumber || '';
    }
    const payload: any = {
      status: pending,
      conversationtypename: convoType?.id || '',
      ordertype: admitOrderType,
      orderdate: formatPimDate(new Date()),
      orderingphysicianid,
      orderingphysicianpersonnumber,
      currentuserid: currentUser.userId.toString(),
      currentuserpersonnumber: currentUser.personNumber,
      currentuserclearscriptslogin: currentUser.clearScriptsLogin,
      patientnumber: patient?.patientNumber,
      patientid: patient?.id,
      patientprogressnoteid: savedNote?.id.toString() || '',
      approvalid: savedApproval?.id || '',
    };

    updateMaintStatus(payload);
  };

  // If Medical Change Verbal Order => update maintain status with pending
  useEffect(() => {
    const okToUpdateMaint =
      orderCreated &&
      orderType === MMD &&
      hasMedOrderError &&
      !hasMedOrderError();
    if (okToUpdateMaint) {
      let orderingphysicianid = admitPhysician?.id.toString() || '';
      let orderingphysicianpersonnumber = admitPhysician?.personNumber || '';
      if (isPhysicianRoleGroup) {
        orderingphysicianid = currentUser?.npi;
        orderingphysicianpersonnumber = currentUser?.personNumber || '';
      }
      const payload: any = {
        status: pending,
        conversationtypename: convoType?.id,
        ordertype: verbalOrderType,
        orderdate: formatPimDate(new Date()),
        orderingphysicianid,
        orderingphysicianpersonnumber,
        currentuserid: currentUser.userId.toString(),
        currentuserpersonnumber: currentUser.personNumber,
        currentuserclearscriptslogin: currentUser.clearScriptsLogin,
        patientnumber: savedNote?.patientnumber,
        patientid: savedNote?.patientid,
        patientprogressnoteid: savedNote?.id.toString(),
        approvalid: savedApproval?.id,
      };
      updateMaintStatus(payload);
    } else if (hasMedOrderError && hasMedOrderError()) {
      doMedOrderCleanup && doMedOrderCleanup();
      updateMedOrderError && updateMedOrderError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCreated]);

  // If completeMaintStatus, update maintain status with complete
  useEffect(() => {
    if (completeMaintStatus) {
      const payload: any = {
        status: complete,
      };
      updateMaintStatus(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeMaintStatus]);

  // agency can override the order verification process
  const overrideVerifOrderBins: string = window.env
    ?.overrideVerificationOrderBins as string;
  // if true NO verification step
  const isOverrideVerifOrderAgency = getOverrideAgency(
    currentUser,
    overrideVerifOrderBins,
  );

  return (
    <ProgressNotesContext.Provider
      value={{
        closeMenu,
        menuType,
        carePlanFrequencies: planFrequencies,
        medications: activeMeds,
        orderTypes: ordertypes,
        standingOrders: standingorders,
        dischargeTypes: dischargetypes,
        dischargeMapping: dischargemapping,
        disOrderMap: isDisOrderMap,
        locTypes: loctypes,
        idgMeeting: nextIDGMeeting,
        LOCtype: patient ? LOCtype || (patient.loc as LOCType) || 'RTN' : null,
        closeTagMenu,
        resetWorkflow,
        cancelWorkflow: cancelWorkflowFunction,
        effectiveDate,
        orderCreated,
        orderDisabled,
        savePSO,
        apiError,
        patientCarePlanProblems,
        ordersGlobalMap,
        ordersGlobalMapEffectiveDate,
        psoText,
        approvalDateError,
        updatePnCount,
        facilityOptions,
        // frequencyOptions: freqOptions,
        periodOptions: durationOptions,
        roleGroupOptions: roleGroupsGlobal as any,
        facilityType,
        dischargeReason,
        noteStarted,
        emptyNote,
        admitPhysicianList,
        rgHash,
        isOverrideVerifOrderAgency,
        checkIfPECOSEligible,
        pecosOpen,
        attendingPhysician: attPhysician,
        pecosPhysicians,
        pickerDialogHeight,
        isPhysicianRoleGroup,
        updateDisMaintainStatus,
        handleGenericOrder,
        handleSendApproval,
        dispatch,
      }}
    >
      {children}
    </ProgressNotesContext.Provider>
  );
};

export default ProgressNotesContextProvider;
